import { useCallback, useRef, useState } from 'react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import type {
  CreateAccessRequestBody,
  CreateAccessRequestResponse,
  RequestableRole,
  RequestableSchool,
  RequestableSchoolsResponse,
} from '@ucp/web/lib';
import { loginRequest } from '../auth/authConfig';
import { NotSignedInError } from './useApiFetch';

/**
 * Self-service access-request hook (PRO-0580 / ADR-036, Phase 1.8).
 *
 * Deliberately acquires its MSAL token DIRECTLY (mirroring `useCurrentUser`) rather
 * than via `useApiFetch`: the zero-role gate path (`AccessNotConfiguredView`) renders
 * OUTSIDE `SchoolContextProvider` (that provider mounts only on the access-configured
 * shell path), so `useApiFetch`'s `useSchoolContext` dependency would throw there.
 * Both Inc-1 endpoints are authn-only / central-scoped and take `schoolId` explicitly
 * in the payload, so no `x-ucp-school-id` header is needed.
 *
 * Surfaces `loadSchools()` (the TD-9 zero-role picker) and `submit()` with a simple
 * `idle | submitting | success | error` status. A client `idempotencyKey`
 * (`crypto.randomUUID`) makes retries safe (the server pending-unique index is the
 * authoritative arbiter, SF-001). No free-text is persisted client-side.
 */

const apiBaseUrl = import.meta.env.DEV ? '/api' : (import.meta.env.VITE_API_BASE_URL ?? '');

export type RequestAccessState =
  | { status: 'idle' }
  | { status: 'submitting' }
  | { status: 'success'; reference: string; outcome: 'pending' | 'auto_closed' }
  | { status: 'error'; message: string };

export interface SubmitAccessRequestFields {
  readonly requestedRole: RequestableRole;
  readonly schoolId: string;
  readonly reason: string;
}

function messageForStatus(status: number, apiMessage?: string): string {
  switch (status) {
    case 400:
      return apiMessage ?? 'Please check the form and try again.';
    case 429:
      return 'You have sent several requests recently — please try again shortly.';
    case 503:
      return 'Access requests are temporarily unavailable. Please try again later.';
    default:
      return 'Sorry, your request could not be sent. Please try again.';
  }
}

export interface UseRequestAccess {
  state: RequestAccessState;
  loadSchools: () => Promise<RequestableSchool[]>;
  submit: (fields: SubmitAccessRequestFields) => Promise<void>;
  reset: () => void;
}

export function useRequestAccess(): UseRequestAccess {
  const { instance, accounts } = useMsal();
  const account = accounts[0] ?? null;
  const accountId = account?.homeAccountId ?? null;
  const accountRef = useRef(account);
  accountRef.current = account;

  const [state, setState] = useState<RequestAccessState>({ status: 'idle' });
  const inFlight = useRef(false);

  const reset = useCallback(() => {
    inFlight.current = false;
    setState({ status: 'idle' });
  }, []);

  const authedFetch = useCallback(
    async (path: string, init?: RequestInit): Promise<Response | null> => {
      const current = accountRef.current;
      if (!current) {
        throw new NotSignedInError();
      }
      let accessToken: string;
      try {
        const result = await instance.acquireTokenSilent({ ...loginRequest, account: current });
        accessToken = result.accessToken;
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          await instance.acquireTokenRedirect({ ...loginRequest, account: current });
          return null;
        }
        throw error;
      }
      const headers = new Headers(init?.headers);
      headers.set('Authorization', `Bearer ${accessToken}`);
      if (init?.body !== undefined && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
      }
      return fetch(`${apiBaseUrl}${path}`, { ...init, headers });
    },
    [accountId, instance],
  );

  const loadSchools = useCallback(async (): Promise<RequestableSchool[]> => {
    const response = await authedFetch('/access-requests/schools');
    if (!response || !response.ok) {
      return [];
    }
    const data = (await response.json()) as RequestableSchoolsResponse;
    return [...data.schools];
  }, [authedFetch]);

  const submit = useCallback(
    async (fields: SubmitAccessRequestFields) => {
      if (inFlight.current) {
        return;
      }
      inFlight.current = true;
      setState({ status: 'submitting' });

      const body: CreateAccessRequestBody = {
        requestedRole: fields.requestedRole,
        schoolId: fields.schoolId,
        ...(fields.reason.trim() ? { reason: fields.reason.trim() } : {}),
        idempotencyKey: crypto.randomUUID(),
      };

      try {
        const response = await authedFetch('/access-requests', {
          method: 'POST',
          body: JSON.stringify(body),
        });
        if (!response) {
          setState({ status: 'error', message: 'Re-authentication required. Please try again.' });
          return;
        }
        if (!response.ok) {
          let apiMessage: string | undefined;
          try {
            const err = (await response.json()) as { message?: string };
            apiMessage = typeof err.message === 'string' ? err.message : undefined;
          } catch {
            apiMessage = undefined;
          }
          setState({ status: 'error', message: messageForStatus(response.status, apiMessage) });
          return;
        }
        const data = (await response.json()) as CreateAccessRequestResponse;
        setState({ status: 'success', reference: data.reference, outcome: data.status });
      } catch (error) {
        const message =
          error instanceof NotSignedInError
            ? error.message
            : 'Sorry, your request could not be sent. Please try again.';
        setState({ status: 'error', message });
      } finally {
        inFlight.current = false;
      }
    },
    [authedFetch],
  );

  return { state, loadSchools, submit, reset };
}
